import React, { useState, useRef, useCallback, useEffect } from "react";
import { HashLink } from "react-router-hash-link";
import { Link } from "react-router-dom";
import CalculatorTemplate from "../../components/calculators/CalculatorTemplate";
import CalculatorPanel from "../../components/calculators/CalculatorPanel";
import CalculatorGenderSelector from "../../components/calculators/CalculatorGenderSelector";
import CalculatorInput from "../../components/calculators/CalculatorInput";
import HeightInputGroup from "../../components/calculators/HeightInputGroup";
import WeightInputGroup from "../../components/calculators/WeightInputGroup";
import CreatinineInputGroup from "../../components/calculators/CreatinineInputGroup";
import {
  useHeightInput,
  useWeightInput,
  useSerumCreatinineInput,
} from "../../hooks/calculatorHooks";
import validateHeightField from "../../utils/calculators/validateHeightField";
import { getHeightInCm } from "../../utils/lengthConverter";
import { getWeightInKg } from "../../utils/weightConverter";
import CalculatorResults from "../../components/calculators/CalculatorResults";
import styles from "../../components/calculators/calculator.module.scss";
import useErrorFocus from "../../hooks/useErrorFocus";

import { calculateCrc } from "../../utils/calculators/crc";
import {
  checkNum,
  saneAge,
  saneSerum,
  saneWeight,
} from "../../utils/calculators/validation";
import { rounding } from "../../utils/calculators/general";
import { forceDropdownProp } from "./forceDropdownProp";
import crcCalcImage from "./images/etg_abg15_appendix_figure1_adult-renal-function.png";

const INITIAL_INPUTS = {
  sex: "",
  age: "",
};

function CrcCalcApp(props) {
  const { isWebLayout } = props;
  const [inputs, setInputs] = useState({ ...INITIAL_INPUTS });
  const [result, setResult] = useState("");
  const [errors, setErrors] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const resultsRef = useRef(null);
  const calcDetailsRef = useRef(null);
  const [forceDropdown, setForceDropdown] = React.useState(false);

  useEffect(() => {
    setForceDropdown(forceDropdownProp(props));
  }, [props]);

  // Define field order for error focus management
  const fieldOrder = ["sex", "height", "weight", "age", "serumCreatinine"];

  // Use the global error focus hook
  useErrorFocus(errors, fieldOrder);

  const {
    height,
    heightUnit,
    feet,
    inches,
    handleHeightChange,
    handleFeetChange,
    handleInchesChange,
    handleUnitChange,
    clearHeightFields,
    unitOptions: heightUnits,
  } = useHeightInput("cm");

  const {
    weightValue,
    weightUnit,
    handleWeightChange,
    handleWeightUnitChange,
    clearWeightFields,
    unitOptions: weightUnitOptions,
  } = useWeightInput();

  const {
    serumCreatinineValue,
    serumCreatinineUnit,
    handleSerumCreatinineChange,
    handleSerumCreatinineUnitChange,
    clearSerumCreatinineFields,
    unitOptions: creatinineUnitOptions,
  } = useSerumCreatinineInput();

  const wrappedHeightChange = useCallback(
    (e) => handleHeightChange(e, setErrors),
    [handleHeightChange],
  );
  const wrappedFeetChange = useCallback(
    (e) => handleFeetChange(e, setErrors),
    [handleFeetChange],
  );
  const wrappedInchesChange = useCallback(
    (e) => handleInchesChange(e, setErrors),
    [handleInchesChange],
  );
  const wrappedUnitChange = useCallback(
    (e) => handleUnitChange(e, setErrors),
    [handleUnitChange],
  );
  const wrappedWeightChange = useCallback(
    (e) => handleWeightChange(e, setErrors),
    [handleWeightChange],
  );
  const wrappedWeightUnitChange = useCallback(
    (e) => handleWeightUnitChange(e, setErrors),
    [handleWeightUnitChange],
  );
  const wrappedCreatinineChange = useCallback(
    (e) => handleSerumCreatinineChange(e, setErrors),
    [handleSerumCreatinineChange],
  );
  const wrappedCreatinineUnitChange = useCallback(
    (e) => handleSerumCreatinineUnitChange(e, setErrors),
    [handleSerumCreatinineUnitChange],
  );

  const handleInputChange = (key, value) => {
    setInputs((prev) => ({
      ...prev,
      [key]: value.replace?.(/^0+/, "") ?? value,
    }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  function validateFields() {
    const errs = {};
    if (!inputs.sex) errs.sex = "Please select a sex";

    const heightError = validateHeightField(heightUnit, height, feet, inches);
    if (heightError) errs.height = heightError;

    const weightCheck = checkNum(weightValue, "weight");
    const weightSane = saneWeight(weightValue, weightUnit);
    if (weightCheck !== "") {
      errs.weight = weightCheck;
    } else if (weightSane !== "") {
      errs.weight = weightSane;
    }

    const ageCheck = checkNum(inputs.age, "age");
    const ageSane = saneAge(inputs.age);
    if (ageCheck !== "") errs.age = ageCheck;
    else if (ageSane !== "") errs.age = ageSane;

    const serumCheck = checkNum(serumCreatinineValue, "serum creatinine");
    const serumSane = saneSerum(serumCreatinineValue, serumCreatinineUnit);
    if (serumCheck !== "") errs.serumCreatinine = serumCheck;
    else if (serumSane !== "") errs.serumCreatinine = serumSane;

    return errs;
  }

  function handleClear() {
    setInputs({ ...INITIAL_INPUTS });
    clearHeightFields();
    clearWeightFields();
    clearSerumCreatinineFields();
    setResult("");
    setShowResult(false);
    setErrors({});
  }

  function handleCalculate(e) {
    e.preventDefault();
    const errs = validateFields();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setShowResult(false);
      setResult("");
      setShowDetails(false);
      return;
    }
    const heightCm = getHeightInCm(heightUnit, height, feet, inches);
    const calcResult = calculateCrc({
      sex: inputs.sex,
      heightCm: heightCm,
      actualBodyWeightKg: getWeightInKg(weightValue, weightUnit),
      age: Number(inputs.age),
      serumCreatinine: Number(serumCreatinineValue),
      serumCreatinineUnit: serumCreatinineUnit,
    });

    if (calcResult && calcResult.creatinineClearance) {
      setResult({
        creatinineClearance: rounding(
          calcResult.creatinineClearance,
          0,
        ).toString(),
        weightTypeForCrCl: calcResult.weightTypeForCrCl,
        bmi: calcResult.bmi,
        bmiCategory: calcResult.bmiCategory,
      });
    } else {
      setResult("");
    }
    setShowResult(!!calcResult?.creatinineClearance);
    setShowDetails(false);
    setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  }

  const panel = (
    <CalculatorPanel header="Calculator" onClear={handleClear} errors={{}}>
      <CalculatorGenderSelector
        value={inputs.sex}
        onChange={(v) => handleInputChange("sex", v)}
        error={errors.sex}
        btnClass={styles.selectorBtn}
        btnActiveClass={styles.selectorBtnActive}
      />
      <HeightInputGroup
        idPrefix="crc"
        height={height}
        heightUnit={heightUnit}
        feet={feet}
        inches={inches}
        onHeightChange={wrappedHeightChange}
        onFeetChange={wrappedFeetChange}
        onInchesChange={wrappedInchesChange}
        onUnitChange={wrappedUnitChange}
        error={errors.height}
        unitOptions={heightUnits}
        isWebLayout={isWebLayout}
        forceDropdown={forceDropdown}
      />
      <WeightInputGroup
        idPrefix="crc"
        label="Actual body weight"
        weightValue={weightValue}
        weightUnit={weightUnit}
        onWeightChange={wrappedWeightChange}
        onWeightUnitChange={wrappedWeightUnitChange}
        error={errors.weight}
        unitOptions={weightUnitOptions}
        isWebLayout={isWebLayout}
        forceDropdown={forceDropdown}
      />
      <CalculatorInput
        label="Age"
        id="crc-age"
        value={inputs.age}
        onChange={(e) => handleInputChange("age", e.target.value)}
        error={errors.age}
        unit="years"
        inputProps={{
          type: "number",
          min: 0,
          autoComplete: "off",
        }}
        isWebLayout={isWebLayout}
      />
      <CreatinineInputGroup
        idPrefix="crc"
        creatinineValue={serumCreatinineValue}
        creatinineUnit={serumCreatinineUnit}
        onCreatinineChange={wrappedCreatinineChange}
        onCreatinineUnitChange={wrappedCreatinineUnitChange}
        error={errors.serumCreatinine}
        unitOptions={creatinineUnitOptions}
        isWebLayout={isWebLayout}
        forceDropdown={forceDropdown}
      />
      <div className={styles.calcBtnRow}>
        <button
          className={styles.calcBtn}
          type="submit"
          onClick={handleCalculate}
        >
          Calculate
        </button>
      </div>
    </CalculatorPanel>
  );

  const description = (
    <div className={isWebLayout ? "" : styles.infoBlock}>
      <p>
        This creatinine clearance calculator should only be used for patients 18
        years and older.
      </p>
      <p>
        The calculator does not accurately predict creatinine clearance in
        patients who have unstable renal function (eg patients in intensive
        care, patients with acute renal impairment, patients with febrile
        neutropenia); a measured (urinary) creatinine clearance is most accurate
        in this situation.
      </p>
      <p>
        The calculator should not be used for patients who have low muscle mass
        (eg cachectic patients).
      </p>
    </div>
  );

  const calculationDetails = showResult ? (
    <div ref={calcDetailsRef} className={styles.calculationDetailsBlock}>
      <div className={styles.calculationDetailsText}>
        {(() => {
          const weightType = result?.weightTypeForCrCl || "";
          const bmiCategory = result?.bmiCategory || "";
          const bmi = result?.bmi;
          return (
            <>
              The creatinine clearance{" "}
              {result?.creatinineClearance &&
              Number(result.creatinineClearance) > 120
                ? "more than 120 mL/min"
                : `${result?.creatinineClearance || "?"} mL/min`}{" "}
              has been calculated using the patient's{" "}
              {weightType || "[weight type]"}.{" "}
              {weightType
                ? weightType.charAt(0).toUpperCase() + weightType.slice(1)
                : "Weight type"}{" "}
              was used because the patient is classified as{" "}
              {bmiCategory || "[BMI category]"} based on a BMI of
              {bmi ? ` ${bmi.toFixed(1)}` : " ?"} kg/m<sup>2</sup>.<br />
              <br />
              It provides an estimate of creatinine clearance using the
              Cockcroft-Gault formula.
            </>
          );
        })()}
      </div>
      <div className={styles.calculationDetailsFormula}>
        <img
          src={crcCalcImage}
          alt="Cockcroft-Gault formula diagram"
          className={styles.calculationDetailsImage}
        />
      </div>
      <div className={styles.calculationDetailsText}>
        Although actual body weight is entered into the calculator, the weight
        used in the calculation of creatinine clearance depends on whether the
        patient (based on their{" "}
        <HashLink
          to="/viewTopic?etgAccess=true&guidelinePage=Cardiovascular&topicfile=cardiovascular-disease-risk-stratification&guidelinename=auto&sectionId=c_CVG_Excess-body-weight-obesity-and-atherosclerotic-cardiovascular-disease-risktopic_6#c_CVG_Excess-body-weight-obesity-and-atherosclerotic-cardiovascular-disease-risktopic_6"
          className={styles.resultLink}
        >
          Body Mass Index [BMI]
        </HashLink>
        ) is underweight, healthy weight or overweight.
        <br />
        <br />
        For patients who are underweight (BMI less than 18.5 kg/m<sup>2</sup>),
        actual body weight is used in the calculator.
        <br />
        <br />
        For patients who are healthy weight (BMI 18.5 to less than 25 kg/m
        <sup>2</sup>),{" "}
        <Link to="/calculator/ibwcalc" className={styles.resultLink}>
          ideal body weight
        </Link>{" "}
        is used in the calculator.
        <br />
        <br />
        For patients who are overweight (BMI 25 kg/m<sup>2</sup> or more),{" "}
        <HashLink
          to="/viewTopic?etgAccess=true&guidelinePage=Antibiotic&topicfile=bartonella-infections&guidelinename=auto&sectionId=c_ABG_Aminoglycoside-use-in-special-patient-groups_topic_5#c_ABG_Aminoglycoside-use-in-special-patient-groups_topic_5"
          className={styles.resultLink}
        >
          adjusted body weight
        </HashLink>{" "}
        is used in the calculator.
      </div>
    </div>
  ) : null;

  const handleExpandDetails = () => {
    setShowDetails((prev) => !prev);
    setTimeout(() => {
      if (calcDetailsRef.current && !showDetails) {
        calcDetailsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  };

  const results = (
    <div ref={resultsRef} data-testid="results-section">
      <CalculatorResults
        label="Creatinine clearance"
        value={result?.creatinineClearance || result}
        unit={result?.creatinineClearance || result ? "mL/min" : undefined}
        showResult={showResult}
        expandableContent={calculationDetails}
        onExpand={handleExpandDetails}
        isWebLayout={isWebLayout}
        footnote={null}
        expandedLabel="Hide calculation"
      />
    </div>
  );

  const calculatorTitle = "Creatinine clearance calculator for adults";

  return (
    <CalculatorTemplate
      title={calculatorTitle}
      description={description}
      panel={panel}
      results={results}
      isWebLayout={isWebLayout}
    />
  );
}

export default CrcCalcApp;
